import { Trans } from '@lingui/macro';
import { Button, Flex, Image, TFCard, TFText } from '@tf/ui';
import type React from 'react';
import { useApplication } from '@/components/application';
import { MarkdownText, useGetConnectionLabels } from '@/components/shared';
import { useDefinitions } from '@/core/providers';

import flatStructureImage from './assets/flat_structure_onboarding_image.svg';
import graphStructureImage from './assets/graph_structure_onboarding_image.svg';

interface Props {
	structureType: 'FLAT' | 'GRAPH';
	onSubmit: () => void;
	onCancel: () => void;
	label: string;
	connectionKind: string;
}

export const OnboardingStructureCard: React.FC<Props> = ({
	structureType,
	onSubmit,
	onCancel,
	label,
	connectionKind,
}) => {
	const { defs } = useDefinitions();
	const application = useApplication();
	const getConnectionLabels = useGetConnectionLabels();
	const getLabelsForEntity = getConnectionLabels(application.account.entityKind);
	const connectionDescription =
		defs.accounts[application.account.entityKind].connections?.[connectionKind]?.description;

	return (
		<TFCard maw="800px" innerPadding="24px">
			<Image src={structureType === 'FLAT' ? flatStructureImage : graphStructureImage} />
			<TFText fz="32px" fw="600" m="32px 0 12px" lh="32px">
				{label}
			</TFText>
			{connectionDescription && (
				<TFText mb="12px">
					<MarkdownText text={connectionDescription} />
				</TFText>
			)}
			<TFText c="gray.6" fz="14px" lh="20px" mb="32px">
				<Trans id="structure.onboardingStructureCard.description">Please provide details, if relevant.</Trans>
			</TFText>
			<Flex>
				<Button variant="light" onClick={onCancel}>
					<Trans id="structure.onboardingStructureCard.cancelButton.label">Skip this step (not recommended)</Trans>
				</Button>
				<Button onClick={onSubmit} ml="12px">
					<Trans id="structure.onboardingStructureCard.submitButton.label">Add</Trans>
					{getLabelsForEntity(connectionKind)?.singular?.toLowerCase()}
				</Button>
			</Flex>
		</TFCard>
	);
};
